import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Share2, BookOpen, TrendingUp, CalendarDays, Clock3, MapPin, UtensilsCrossed, Ticket, CheckCircle2, ExternalLink, Send } from "lucide-react";
import { api } from "@/lib/api";

const NAVY = "#07143F";
const TEAL = "#26A6A1";
const CORAL = "#F26B5E";
const GOLD = "#E5AD3D";
const PAYPAL_LINK = "https://www.paypal.com/ncp/payment/AT7PX82YVBHPN";

const SUPPORT_OPTS = ["Marketing y redes sociales", "Conseguir más clientes", "Finanzas y financiamiento", "Precios y cotizaciones", "Página web / presencia digital", "Registro, licencias o seguros", "Organización y administración", "Networking y conexiones", "Crecimiento de mi negocio", "Apoyo para desarrollar una idea de negocio", "Otro"];

const PILLARS = [
  { Icon: Users, title: "CONECTA", text: "con otros emprendedores", color: TEAL },
  { Icon: Share2, title: "COMPARTE", text: "ideas, recursos y experiencias", color: CORAL },
  { Icon: BookOpen, title: "APRENDE", text: "de la comunidad", color: GOLD },
  { Icon: TrendingUp, title: "CRECE", text: "tu negocio con apoyo real", color: TEAL },
];

const inp = "w-full bg-white rounded-xl border border-slate-300 px-4 py-3.5 text-[16px] outline-none transition focus:ring-2 focus:ring-[#26A6A1] focus:border-transparent";
const Lbl = ({ children, req }) => <label className="block text-[15px] font-semibold mb-1.5" style={{ color: NAVY }}>{children}{req && <span className="text-red-500"> *</span>}</label>;

export default function Mesa() {
  const [d, setD] = useState({ nombre: "", email: "", phone: "", business_name: "", business_type: "", support_needs: [], support_needs_other: "", expectations: "", dinner_topic: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [err, setErr] = useState("");

  useEffect(() => { document.title = "La Mesa Emprendedora | Alma y Lengua"; }, []);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email);
  const valid = d.nombre.trim() && emailValid && d.phone.trim() && (!d.support_needs.includes("Otro") || d.support_needs_other.trim());

  const toggleNeed = (o) => set("support_needs", d.support_needs.includes(o) ? d.support_needs.filter((x) => x !== o) : [...d.support_needs, o]);

  const submit = async () => {
    if (!valid) { setErr("Por favor completa los campos obligatorios (*): nombre, correo electrónico válido y teléfono."); window.scrollTo({ top: document.getElementById("mesa-form").offsetTop - 80, behavior: "smooth" }); return; }
    setStatus("sending"); setErr("");
    try { await api.post("/mesa-registrations", d); setStatus("success"); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch { setStatus("error"); setErr("Ocurrió un error al enviar. Por favor intenta de nuevo."); }
  };

  const PayButton = ({ className = "" }) => (
    <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer" data-testid="mesa-paypal-link"
      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl transition hover:-translate-y-0.5 ${className}`}
      style={{ background: CORAL }}>
      <Ticket size={22} /> RESERVAR MI ESPACIO – $35 <ExternalLink size={18} />
    </a>
  );

  if (status === "success") {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-16 bg-white">
        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "backOut" }}
          className="max-w-xl w-full rounded-3xl shadow-2xl p-8 md:p-10 text-center border-4" style={{ borderColor: GOLD }} data-testid="mesa-success">
          <div className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6 text-white" style={{ background: TEAL }}><CheckCircle2 size={44} /></div>
          <h1 className="text-2xl md:text-3xl font-extrabold mb-4" style={{ color: NAVY }} data-testid="mesa-success-title">¡Gracias por registrarte para La Mesa Emprendedora! 🎉</h1>
          <p className="text-ink/70 leading-relaxed mb-6">Solo falta un paso: completa tu pago de $35 para confirmar tu espacio. Estamos preparando una noche de buena comida, conexiones, conversaciones y oportunidades para crecer juntos.</p>
          <div className="rounded-2xl p-5 mb-6 text-left" style={{ background: "#F0FDFA" }} data-testid="mesa-payment-box">
            <p className="font-extrabold mb-2" style={{ color: NAVY }}>¡Ya casi estás listo/a!</p>
            <p className="text-sm text-ink/70 mb-1">El costo de La Mesa Emprendedora es de <strong>$35 por persona</strong> e incluye la cena.</p>
            <p className="text-sm text-ink/70 mb-1">Tu espacio queda confirmado una vez que completes tu pago.</p>
            <p className="text-sm font-bold" style={{ color: CORAL }}>Cupos limitados.</p>
          </div>
          <PayButton className="w-full mb-3" />
          <p className="text-sm text-ink/50">El pago confirma tu reservación.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(150deg, ${NAVY} 60%, #0E2A63)` }}>
        <div className="max-w-3xl mx-auto px-5 pt-10 pb-14 text-center text-white relative">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-white grid place-items-center font-extrabold text-lg shadow" style={{ color: NAVY }}>AL</div>
            <div className="leading-tight text-left">
              <span className="block font-extrabold text-[15px] tracking-tight">Alma Y Lengua</span>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">Bilingual Learning Center</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-bold tracking-wide mb-4" style={{ color: GOLD }} data-testid="mesa-tagline">Conecta • Comparte • Aprende • Crece</p>
            <h1 className="font-extrabold text-4xl md:text-6xl leading-tight mb-2" data-testid="mesa-title">La Mesa Emprendedora</h1>
            <p className="text-lg md:text-xl font-semibold mb-6" style={{ color: TEAL }}>Una cena para emprendedores y dueños de negocios</p>
            <p className="text-white/85 leading-relaxed max-w-xl mx-auto">
              ¡Bienvenido/a a La Mesa Emprendedora! Este encuentro fue creado para emprendedores y dueños de negocios que desean conectar, compartir experiencias, encontrar recursos y crecer juntos.
              Ven a compartir una cena, conocer a otros emprendedores de nuestra comunidad y participar en conversaciones sobre negocios, desafíos, recursos y oportunidades.
            </p>
            <p className="font-bold mt-4" style={{ color: GOLD }}>Buena comida. Buenas conversaciones. Grandes oportunidades.</p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {[
              { Icon: CalendarDays, text: "Viernes, 16 de octubre de 2026" },
              { Icon: Clock3, text: "6:30 PM – 8:00 PM" },
              { Icon: MapPin, text: "24 S 3rd St, Unit 105, Oxford, PA" },
              { Icon: UtensilsCrossed, text: "$35 por persona · Cena incluida" },
            ].map(({ Icon, text }) => (
              <div key={text} className="bg-white/95 rounded-full px-4 py-2.5 flex items-center gap-2 shadow-lg" data-testid="mesa-chip">
                <Icon size={16} style={{ color: CORAL }} />
                <span className="text-xs font-bold" style={{ color: NAVY }}>{text}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-4">
            <span className="rounded-full px-4 py-1.5 text-xs font-extrabold text-white" style={{ background: CORAL }}>Cupos limitados</span>
            <span className="rounded-full px-4 py-1.5 text-xs font-extrabold" style={{ background: GOLD, color: NAVY }}>Registro requerido</span>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-3xl mx-auto px-5 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="mesa-pillars">
          {PILLARS.map(({ Icon, title, text, color }, i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-white rounded-3xl border-2 p-5 text-center shadow-sm" style={{ borderColor: "#EEF1F7" }}>
              <div className="w-12 h-12 rounded-2xl grid place-items-center mx-auto mb-3 text-white" style={{ background: color }}><Icon size={22} /></div>
              <p className="font-extrabold text-sm" style={{ color: NAVY }}>{title}</p>
              <p className="text-xs text-ink/60 mt-1">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section id="mesa-form" className="max-w-2xl mx-auto px-5 pb-14">
        <div className="rounded-3xl border-2 shadow-sm p-6 md:p-8" style={{ borderColor: "#EEF1F7", background: "#FDFEFF" }} data-testid="mesa-form-card">
          <h2 className="font-extrabold text-2xl mb-1" style={{ color: NAVY }}>Registro</h2>
          <p className="text-sm text-ink/60 mb-6">Completa tus datos para reservar tu lugar en la cena.</p>
          {err && <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-5 py-4 text-sm" data-testid="mesa-error">{err}</div>}
          <form onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
            <div className="mb-5"><Lbl req>1. Nombre y apellido</Lbl><input data-testid="mesa-nombre" className={inp} value={d.nombre} onChange={(e) => set("nombre", e.target.value)} /></div>
            <div className="mb-5"><Lbl req>2. Correo electrónico</Lbl><input type="email" data-testid="mesa-email" className={inp} value={d.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="mb-5"><Lbl req>3. Número de teléfono</Lbl><input type="tel" data-testid="mesa-phone" className={inp} value={d.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="mb-5">
              <Lbl>4. Nombre de tu negocio</Lbl>
              <input data-testid="mesa-business" className={inp} value={d.business_name} onChange={(e) => set("business_name", e.target.value)} />
              <p className="text-xs text-ink/50 mt-1.5">Si todavía estás desarrollando tu idea de negocio, puedes escribir “En desarrollo”.</p>
            </div>
            <div className="mb-5"><Lbl>5. ¿A qué se dedica tu negocio?</Lbl><input data-testid="mesa-type" className={inp} value={d.business_type} onChange={(e) => set("business_type", e.target.value)} /></div>
            <div className="mb-5">
              <Lbl>6. ¿Qué tipo de apoyo o recursos estás buscando actualmente?</Lbl>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="mesa-support">
                {SUPPORT_OPTS.map((o) => {
                  const on = d.support_needs.includes(o);
                  return (
                    <label key={o} className="flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer" style={{ borderColor: on ? TEAL : "#e2e8f0", background: on ? "#F0FDFA" : "#fff" }}>
                      <input type="checkbox" checked={on} onChange={() => toggleNeed(o)} className="w-5 h-5 shrink-0" style={{ accentColor: TEAL }} />
                      <span className="text-[14px] text-ink/80">{o}</span>
                    </label>
                  );
                })}
              </div>
              {d.support_needs.includes("Otro") && <input data-testid="mesa-support-other" className={`${inp} mt-3`} placeholder="Especifica “Otro”" value={d.support_needs_other} onChange={(e) => set("support_needs_other", e.target.value)} />}
            </div>
            <div className="mb-5">
              <Lbl>7. ¿Qué esperas obtener de La Mesa Emprendedora?</Lbl>
              <p className="text-xs text-ink/50 mb-1.5">Por ejemplo: conocer a otros emprendedores, encontrar recursos, hacer conexiones, aprender algo nuevo, encontrar posibles colaboradores o recibir apoyo con un reto de tu negocio.</p>
              <textarea rows={4} data-testid="mesa-expectations" className={inp} value={d.expectations} onChange={(e) => set("expectations", e.target.value)} />
            </div>
            <div className="mb-6"><Lbl>8. ¿Hay algún tema, pregunta o desafío de tu negocio que te gustaría conversar durante la cena? <span className="font-normal text-ink/40">(opcional)</span></Lbl><textarea rows={3} data-testid="mesa-dinner-topic" className={inp} value={d.dinner_topic} onChange={(e) => set("dinner_topic", e.target.value)} /></div>
            <button type="submit" disabled={status === "sending"} data-testid="mesa-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl disabled:opacity-60" style={{ background: NAVY }}>
              <Send size={20} /> {status === "sending" ? "Enviando..." : "Completar registro"}
            </button>
          </form>
        </div>

        <p className="text-center font-extrabold text-xl mt-12 mb-4" style={{ color: NAVY }} data-testid="mesa-footer-line">Tu negocio también pertenece aquí.</p>
        <p className="text-center text-sm text-ink/50 pb-8">Alma y Lengua Bilingual Learning Center · 24 S 3rd St, Unit 105, Oxford, PA</p>
      </section>
    </div>
  );
}
